import { useEffect, useRef, useState } from "react";
import type { Identity } from "../api/client";
import { Icon } from "../components/Icon";
import { useTheme } from "./useTheme";

interface TopbarProps {
  identity: Identity;
  onSignOut: () => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** Collapses the desktop rail, or opens the mobile drawer. */
  onToggleSidebar: () => void;
  sidebarExpanded: boolean;
}

/**
 * Of the controls here, only the notifications bell is cosmetic: there
 * is no notification system anywhere in this codebase (no websocket, no
 * poll endpoint, no event table) to back it, so it stays visibly disabled
 * rather than wired to a fabricated count (CLAUDE.md: never claim mock
 * behavior as a production feature). Search, the theme toggle, and the
 * profile menu are all real — search narrows the sidebar's own nav items
 * (there is no backend search to call), the toggle flips a real persisted
 * `data-theme`, and the profile menu shows the identity the API actually
 * returned.
 */
export function Topbar({
  identity,
  onSignOut,
  searchValue,
  onSearchChange,
  onToggleSidebar,
  sidebarExpanded,
}: TopbarProps) {
  const { theme, toggleTheme } = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);

  // Cmd/Ctrl+K focuses the section filter, as the hint inside it says.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close the profile menu on any click outside it.
  useEffect(() => {
    if (!profileOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [profileOpen]);

  const roleLabel = identity.role.replace("_", " ");
  const initial = (identity.email ?? identity.role).charAt(0).toUpperCase();

  return (
    <header className="topbar">
      <div className="topbar__left">
        <button
          type="button"
          className="topbar__menu-btn"
          onClick={onToggleSidebar}
          aria-label="Toggle sidebar"
          aria-expanded={sidebarExpanded}
        >
          <Icon name="menu" />
        </button>
        <div className="topbar__search-wrap">
          <span className="topbar__search-icon">
            <Icon name="search" />
          </span>
          <input
            ref={searchRef}
            type="search"
            className="topbar__search"
            placeholder="Filter sections…"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            aria-label="Filter dashboard sections"
          />
          <kbd className="topbar__search-kbd" aria-hidden="true">
            ⌘K
          </kbd>
        </div>
      </div>
      <div className="topbar__actions">
        <button
          type="button"
          className="topbar__icon-btn"
          onClick={toggleTheme}
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} />
        </button>
        <button
          type="button"
          className="topbar__icon-btn"
          disabled
          aria-disabled="true"
          title="Notifications — coming soon"
        >
          <Icon name="bell" />
          <span className="sr-only">Notifications (coming soon)</span>
        </button>
        <div className="topbar__profile" ref={profileRef}>
          <button
            type="button"
            className="topbar__profile-trigger"
            onClick={() => setProfileOpen((open) => !open)}
            aria-expanded={profileOpen}
            aria-haspopup="menu"
          >
            <span className="topbar__avatar" aria-hidden="true">
              {initial}
            </span>
            <span className="topbar__role">{roleLabel}</span>
            <span className={`topbar__chevron${profileOpen ? " topbar__chevron--open" : ""}`}>
              <Icon name="chevron-down" size={18} />
            </span>
          </button>
          {profileOpen ? (
            <div className="topbar__profile-menu" role="menu">
              <p className="topbar__profile-role">{roleLabel}</p>
              <p className="topbar__profile-email">{identity.email ?? "No recovery email set"}</p>
              <button type="button" className="topbar__profile-action" role="menuitem" onClick={onSignOut}>
                <Icon name="logout" />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
